// Blog post data — content kept faithful to Jonathan's original LinkedIn voice,
// restructured with H2/H3 headings for scannability + SEO.

export const blogPosts = [
  {
    slug: "kundeerhvervelsesomkostninger-cac-erp",
    title: "Sådan beregner du kundeerhvervelsesomkostninger (CAC) ud fra dine ERP-data",
    metaTitle:
      "CAC: Sådan beregner du kundeerhvervelsesomkostninger ud fra ERP | Valubi",
    metaDescription:
      "Hvor meget koster det at få en ny kunde? Lær hvordan du måler CAC pr. salgskanal i Business Central eller e-conomic — og hvad du gør med tallet.",
    date: "2026-02-08",
    readingTime: "3 min",
    category: "Salg & Marketing",
    keywords: [
      "kundeerhvervelsesomkostninger",
      "CAC",
      "salgskanaler",
      "Business Central",
      "Power BI"
    ],
    excerpt:
      "Hvor meget koster det at få en ny kunde? Du behøver ikke kende prisen på alle kanaler — kun dem du kan skrue op og ned for.",
    content: [
      {
        type: "p",
        text: "Hvor meget koster det at få en ny kunde? Det er et af de mest stillede — og dårligst besvarede — spørgsmål i små og mellemstore virksomheder. Men det er værd at få et svar på, for når du ved det, kan du træffe markant bedre beslutninger om, hvor markedsføringskronerne skal hen."
      },
      {
        type: "h2",
        text: "Du behøver ikke kende prisen på alle kanaler"
      },
      {
        type: "p",
        text: "Kunder kommer ind gennem mange forskellige kanaler — og nogle af dem er nemmere at måle på end andre. Personligt netværk er for eksempel svært at sætte en pris på. Hvor mange kaffemøder kostede den seneste kunde dig? Det er ikke til at sige."
      },
      {
        type: "p",
        text: "Betalte annoncer på den anden side er ret nemme. Du ved præcis hvor meget du har brugt på Google Ads i sidste måned, og du ved hvor mange leads og kunder det blev til. Pointen er: Du behøver ikke kende prisen på alle dine kanaler. Du skal kende prisen på dem, du faktisk kan justere på."
      },
      {
        type: "h2",
        text: "Registrer salgskanalen på hver kunde i ERP-systemet"
      },
      {
        type: "p",
        text: "Det første konkrete skridt er at få registreret salgskanalen på hver eneste nye kunde i dit ERP-system — fx Business Central eller e-conomic. Brug et kundefelt eller en dimension. Så kan du senere filtrere analysen ned til de kanaler, du har en omkostning på."
      },
      {
        type: "h2",
        text: "Sammenlign CAC med kundens livstidsværdi"
      },
      {
        type: "p",
        text: "Når du har CAC pr. kanal, kan du sammenligne den med den gennemsnitlige livstidsværdi (LTV) for de kunder, der kommer ind via samme kanal. Hvis en Google Ads-kunde i gennemsnit koster 8.000 kr at erhverve og giver 35.000 kr i dækningsbidrag over deres levetid, så er det en god forretning. Hvis det er omvendt, ved du nu, at du skal skrue ned."
      },
      {
        type: "h2",
        text: "Det du kan måle, kan du som regel også justere"
      },
      {
        type: "p",
        text: "Det er ikke alt der kan måles. Men heldigvis er det der kan måles ofte også det, der er nemt at skrue op eller ned for. Start med de kanaler du har en regning på, og lad de svært målbare ligge — indtil du har overblik over de andre."
      }
    ]
  },
  {
    slug: "kreditnotaratio-kpi-business-central",
    title: "Kreditnotaratio: KPI'en der afslører hvor virksomheden taber penge",
    metaTitle:
      "Kreditnotaratio: KPI der afslører skjulte tab i Business Central | Valubi",
    metaDescription:
      "Kreditnotaratio = kreditnotabeløb / faktureret beløb. Lær hvilke spørgsmål den besvarer, og hvordan årsagskoder gør den endnu skarpere.",
    date: "2026-02-04",
    readingTime: "3 min",
    category: "KPI",
    keywords: [
      "kreditnotaratio",
      "KPI",
      "Business Central",
      "Power BI",
      "kreditnota"
    ],
    excerpt:
      "Kreditnotaratioen er nem at udregne og endnu nemmere at omsætte til handling. Her er de spørgsmål den besvarer.",
    content: [
      {
        type: "p",
        text: "En af de mest undervurderede KPI'er i mit arbejde med Business Central og Power BI er kreditnotaratioen. Den udregnes simpelt som kreditnotabeløb divideret med faktureret beløb — og den giver svar på nogle vigtige spørgsmål, som er nemme at omsætte til konkret handling."
      },
      {
        type: "h2",
        text: "3 spørgsmål kreditnotaratioen besvarer"
      },
      {
        type: "h3",
        text: "1. Er der varer eller ydelser, der ofte bliver krediteret?"
      },
      {
        type: "p",
        text: "Hvis en bestemt vare har en kreditnotaratio på 25 %, er det værd at stille spørgsmålet: Skal vi overhovedet forhandle den her? Det kan være kvaliteten ikke matcher prisen, eller at varen ikke svarer til kundernes forventning."
      },
      {
        type: "h3",
        text: "2. Er der kunder der systematisk bonger ud?"
      },
      {
        type: "p",
        text: "Nogle kunder klager altid og får altid noget kreditnota. Når du ser ratioen pr. kunde, kan du afgøre om det er jeres egen fejl (dårlig levering, forkert produkt) — eller om det bare er en besværlig kunde, der ikke er rentabel at have."
      },
      {
        type: "h3",
        text: "3. Hvordan har ratioen udviklet sig over tid?"
      },
      {
        type: "p",
        text: "Hvis kreditnotaratioen pludselig stiger, er det et tidligt advarselssignal. Et leverandørproblem, en ny medarbejder der laver fejl, eller et systemfejl der sender forkerte fakturaer ud. Når du har den i Power BI, ser du det inden økonomien tager skade."
      },
      {
        type: "h2",
        text: "Pro tip: Skriv årsagskoder på kreditnotaerne"
      },
      {
        type: "p",
        text: "Hvis du virkelig vil have noget ud af kreditnotaratioen, så indfør årsagskoder på hver enkelt kreditnota. Var det en faktureringsfejl? Et produktproblem? En kunde der returnerede en vare? Med årsagskoder kan du se mønstre og tage handling ved roden — i stedet for bare at konstatere, at I krediterer for meget."
      },
      {
        type: "h2",
        text: "OBS: Almindeligt dækningsbidrag bliver misvisende"
      },
      {
        type: "p",
        text: "Det er værd at bemærke, at det almindelige dækningsbidrag ofte ikke er retvisende, når det kommer til kreditnotaer — specielt når man rapporterer det på vareniveau. Men det er en anden historie, og kræver sin egen artikel."
      }
    ]
  },
  {
    slug: "doedt-lager-quickwin-business-intelligence",
    title: "Dødt lager: Den hurtigste gevinst i Business Intelligence",
    metaTitle:
      "Dødt lager: Den hurtigste BI-gevinst for dig med Business Central | Valubi",
    metaDescription:
      "Dødt lager binder kapital og ender ofte som afskrivning. Lær hvorfor 50 % rabat kan være en fantastisk forretning — og hvordan du undgår nyt dødt lager.",
    date: "2026-01-28",
    readingTime: "3 min",
    category: "Lager",
    keywords: [
      "dødt lager",
      "Business Intelligence",
      "Power BI",
      "Business Central",
      "lagerstyring"
    ],
    excerpt:
      "Den hurtigste gevinst i BI er ikke ny indsigt — det er at få ryddet op i det dødt lager du allerede har.",
    content: [
      {
        type: "p",
        text: "Hvis nogen spørger mig hvad den hurtigste gevinst er ved at indføre Business Intelligence, så er svaret klart: Dødt lager. Altså varer du ikke sælger længere, men som stadig står på lageret og binder kapital."
      },
      {
        type: "h2",
        text: "Hvorfor dødt lager er det hurtigste win"
      },
      {
        type: "p",
        text: "Hvis du ikke får solgt det døde lager snart, ender det med en afskrivning. Og når det alligevel skal afskrives, så er den økonomiske dækningsgrad reelt 100 %. Det betyder, at du kan give massiv rabat — og stadig ende med et bedre resultat, end hvis du ikke gjorde noget."
      },
      {
        type: "h2",
        text: "Eksempel: 1 mio kr i dødt lager"
      },
      {
        type: "p",
        text: "Lad os tage et konkret regnestykke. Forestil dig du har dødt lager for 1 mio kr. Hvis du sælger halvdelen med 50 % rabat, har du \"tjent\" 250.000 kr — set i forhold til situationen, hvor det hele skulle afskrives. Du har frigjort kapital, ryddet plads på lageret, og fået kundekontakt ud af det."
      },
      {
        type: "h2",
        text: "Et alternativt quick win: Slå genbestilling fra"
      },
      {
        type: "p",
        text: "Hvis du ikke har lyst til at køre brandudsalg, så er der et endnu hurtigere win: Gå ind i dit ERP-system og slå genbestilling fra på de døde varer. Så undgår du, at MRP-kørslen foreslår dem til indkøb igen, og at du kommer til at købe flere hjem ved en fejl."
      },
      {
        type: "h2",
        text: "Hvordan finder du dødt lager?"
      },
      {
        type: "p",
        text: "I Power BI kan du nemt lave en simpel rapport, der viser varer der ikke er solgt i x antal måneder, men som stadig har lagerværdi. Det kræver ikke et komplekst data warehouse — det kan være lige så simpelt som et ad hoc-udtræk eller en tilbagevendende rapport, du selv bygger oven på dine ERP-data."
      }
    ]
  },
  {
    slug: "omsaetning-minus-stoerste-kunde-kpi",
    title: "Omsætning minus største kunde: KPI'en der måler din forretnings robusthed",
    metaTitle:
      "Omsætning minus største kunde: Mål din kundekoncentration | Valubi",
    metaDescription:
      "Hvad sker der hvis du mister din største kunde? En simpel KPI viser robustheden i din omsætning — og afslører ofte mere end toplinjen gør.",
    date: "2026-01-21",
    readingTime: "2 min",
    category: "KPI",
    keywords: [
      "kundekoncentration",
      "robusthed",
      "KPI",
      "omsætning",
      "Business Intelligence"
    ],
    excerpt:
      "Samme toplinje to år i træk kan dække over markant forskellige forretninger. Her er KPI'en der viser forskellen.",
    content: [
      {
        type: "p",
        text: "Hvad sker der med din virksomhed, hvis du mister din største kunde i morgen? Det er et spørgsmål jeg selv tænker over, og som mange små virksomheder bør have et tal på."
      },
      {
        type: "h2",
        text: "Den ujævne fordeling i små virksomheder"
      },
      {
        type: "p",
        text: "Som mange andre små virksomheder har jeg ofte en lille håndfuld kunder ad gangen. Det er ikke unormalt at en enkelt kunde står for over halvdelen af min omsætning. Det giver god cashflow her og nu, men det gør også forretningen sårbar — hvis den kunde forsvinder, falder bunden ud af regnskabet."
      },
      {
        type: "h2",
        text: "KPI'en: Omsætning minus største kunde"
      },
      {
        type: "p",
        text: "En simpel KPI jeg bruger til at måle robusthed er: Omsætning minus omsætning fra største kunde. Den er nem at udregne — du tager bare den samlede omsætning og trækker det den største kunde har lagt — men den fortæller noget, som toplinjen ikke fortæller."
      },
      {
        type: "h2",
        text: "Eksempel: 2024 vs 2025"
      },
      {
        type: "p",
        text: "Min samlede omsætning i 2024 og 2025 lå tæt på hinanden. Hvis man kun kiggede på toplinjen, så det ud som om der ikke var sket noget. Men min omsætning minus største kunde var næsten 3 gange højere i 2025. Samme toplinje — men markant bedre robusthed. Det er den slags indsigter Power BI kan gøre synlige hver dag."
      },
      {
        type: "h2",
        text: "Hvad du kan gøre med tallet"
      },
      {
        type: "p",
        text: "Når du følger KPI'en over tid, ser du om virksomheden er ved at blive mere eller mindre afhængig af enkelte kunder. Hvis trenden går den forkerte vej, er det et signal om at investere i salg eller markedsføring til nye segmenter — før du står i den situation, hvor du absolut ikke har råd til at miste den ene kunde."
      }
    ]
  },
  {
    slug: "arbejdsdage-sammenligning-omsaetning-power-bi",
    title: "Arbejdsdage skævvrider dine månedssammenligninger — sådan løser du det",
    metaTitle:
      "Arbejdsdage og omsætning: Sammenlign måneder retfærdigt i Power BI | Valubi",
    metaDescription:
      "November ser ofte dårligere ud end oktober — men ofte er den eneste forskel antal arbejdsdage. Her er KPI'en der retter op på sammenligningen.",
    date: "2026-01-14",
    readingTime: "2 min",
    category: "Power BI",
    keywords: [
      "arbejdsdage",
      "månedssammenligning",
      "Power BI",
      "omsætning",
      "finansrapport"
    ],
    excerpt:
      "Var november virkelig dårligere end oktober — eller var den bare 3 dage kortere? Her er KPI'en der giver dig sandheden.",
    content: [
      {
        type: "p",
        text: "Jeg gætter på, at jeres omsætning var højere i oktober 2025 end i november 2025. Hvorfor? Fordi oktober havde 23 arbejdsdage, hvor november kun havde 20."
      },
      {
        type: "h2",
        text: "En forskel på 15 % der ikke handler om performance"
      },
      {
        type: "p",
        text: "Tre arbejdsdage færre svarer til en forskel på cirka 15 % — uden at I egentlig har lavet noget anderledes. Forskellen handler ikke om helligdage, men om at november havde en weekend mere end oktober. Det ødelægger sammenligningen, hvis man kun ser på de rå tal."
      },
      {
        type: "h2",
        text: "Hvorfor det er et problem"
      },
      {
        type: "p",
        text: "Hvis du som økonomichef eller direktør ser på månedstal og oplever, at \"november var dårlig\", så agerer du måske på det. Måske skruer du op for marketing, måske presser du salgsteamet. Men hvis det rigtige svar er, at november bare var kortere — så har du brugt energi på den forkerte ting."
      },
      {
        type: "h2",
        text: "Løsningen: Omsætning per arbejdsdag"
      },
      {
        type: "p",
        text: "Den hurtige og nemme løsning er at tilføje den gennemsnitlige omsætning per arbejdsdag til din BI-rapport. Du tager den samlede omsætning i måneden og dividerer med antal arbejdsdage. På den måde kan du hurtigt se, om en \"dårlig\" måned faktisk er dårlig, eller om den bare er kortere end den ser ud."
      },
      {
        type: "h2",
        text: "Sådan får du KPI'en i Power BI"
      },
      {
        type: "p",
        text: "I Power BI kan du oprette et beregnet mål, der dividerer SUM(omsætning) med antal arbejdsdage i den valgte periode. Det kræver en datokalender med et \"er arbejdsdag\"-flag — som er standard i et ordentligt data warehouse."
      }
    ]
  }
];

export const getPostBySlug = (slug) => blogPosts.find((p) => p.slug === slug);
